import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useReactFlow } from 'reactflow';
import { setEdgesActionCreator, setSelectedEdgeIds } from '../redux/reducers/global/edge/edgeSlice';
import { setNodesActionCreator, setSelectedNodeIds } from '../redux/reducers/global/node/nodeSlice';
import { HORIZONTAL_ORIENTATION, VERTICAL_ORIENTATION } from '../utilities/constants/editorConstants';

let cloneEdgeIdCounter = 0;

const createUniqueCloneEdgeId = (existingIds = new Set()) => {
  const ids = existingIds instanceof Set ? existingIds : new Set();
  let id = null;
  do {
    cloneEdgeIdCounter += 1;
    const randomPart = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 12);
    id = `clone_edge_${Date.now()}_${cloneEdgeIdCounter}_${randomPart}`;
  } while (ids.has(id));
  ids.add(id);
  return id;
};

export const useNodeSelectionActions = ({ setNodes, cloneNode } = {}) => {
  const dispatch = useDispatch();
  const reactFlow = useReactFlow();
  const selectedNodeIds = useSelector((state) => state.node.selectedNodeIds);
  const selectedNodeId = useSelector((state) => state.node.selectedNodeId);
  const nodes = useSelector((state) => state.node.nodes);
  const edges = useSelector((state) => state.edge.edges);


  const currentNode = useMemo(
    () => (Array.isArray(nodes) ? nodes : []).find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const commitClonedNodes = useCallback((clonedNodes, sourceNodesOverride = null) => {
    const clones = Array.isArray(clonedNodes)
      ? clonedNodes.filter((node) => node && node.id)
      : [];
    if (clones.length === 0) return;

    const clonedIds = clones.map((node) => node.id);
    const clonedIdSet = new Set(clonedIds);
    const rfNodes = reactFlow?.getNodes?.() || [];
    const reduxNodes = Array.isArray(nodes) ? nodes : [];
    const sourceNodes = Array.isArray(sourceNodesOverride)
      ? sourceNodesOverride
      : (rfNodes.length > 0 ? rfNodes : reduxNodes);
    const existingIds = new Set(sourceNodes.map((node) => node?.id).filter(Boolean));
    const nextNodes = [
      ...sourceNodes.map((node) => (
        !node
          ? node
          : clonedIdSet.has(node.id)
          ? { ...node, selected: true }
          : { ...node, selected: false }
      )),
      ...clones
        .filter((node) => !existingIds.has(node.id))
        .map((node) => ({ ...node, selected: true })),
    ];

    try {
      reactFlow?.setNodes?.(nextNodes);
    } catch {}

    setNodes(nextNodes);
    dispatch(setNodesActionCreator({ nodes: nextNodes }));
    dispatch(setSelectedNodeIds(clonedIds));
    dispatch(setSelectedEdgeIds([]));
    return nextNodes;
  }, [dispatch, nodes, reactFlow, setNodes]);

  const cloneSelection = useCallback(() => {
    if (!Array.isArray(selectedNodeIds) || selectedNodeIds.length === 0) return;
    if (typeof cloneNode !== 'function' || typeof setNodes !== 'function') return;

    if (selectedNodeIds.length > 1) {
      const sourceNodes = reactFlow?.getNodes?.() || (Array.isArray(nodes) ? nodes : []);
      const existingNodeIds = new Set((Array.isArray(sourceNodes) ? sourceNodes : [])
        .map((node) => node?.id)
        .filter(Boolean));
      const mapping = {};
      const clonedNodes = [];
      const reservedCloneNames = new Set();
      const selectedNodes = (Array.isArray(sourceNodes) ? sourceNodes : [])
        .filter((node) => selectedNodeIds.includes(node.id))
        .map((node) => JSON.parse(JSON.stringify(node)));

      selectedNodes.forEach((node) => {
        const cloned = cloneNode(node, existingNodeIds, reservedCloneNames);
        if (!cloned) return;
        existingNodeIds.add(cloned.id);
        const clonedName = cloned.data?.attributes?.nombre?.value || cloned.data?.attributes?.name?.value;
        if (typeof clonedName === 'string' && clonedName.trim()) {
          reservedCloneNames.add(clonedName.trim());
        }
        mapping[node.id] = cloned.id;
        clonedNodes.push(cloned);
      });

      const clonedIds = clonedNodes.map((node) => node.id);
      if (clonedIds.length === 0) return;

      const sourceEdges = Array.isArray(edges) ? edges : [];
      const edgesToClone = sourceEdges.filter(
        (edge) => selectedNodeIds.includes(edge.source) && selectedNodeIds.includes(edge.target)
      );

      if (edgesToClone.length > 0) {
        const existingEdgeIds = new Set(sourceEdges.map((edge) => edge?.id).filter(Boolean));
        const clonedEdges = edgesToClone.map((edge) => ({
          ...edge,
          id: createUniqueCloneEdgeId(existingEdgeIds),
          source: mapping[edge.source],
          target: mapping[edge.target],
        }));

        const updatedEdges = [...sourceEdges, ...clonedEdges].map((edge) => JSON.parse(JSON.stringify(edge)));
        dispatch(setEdgesActionCreator({ edges: updatedEdges }));
      }

      commitClonedNodes(clonedNodes, sourceNodes);

      return;
    }

    const sourceNodes = reactFlow?.getNodes?.() || (Array.isArray(nodes) ? nodes : []);
    const sourceNode = (Array.isArray(sourceNodes) ? sourceNodes : []).find((node) => node.id === selectedNodeIds[0])
      || currentNode;
    if (!sourceNode) return;
    const existingNodeIds = new Set((Array.isArray(sourceNodes) ? sourceNodes : [])
      .map((node) => node?.id)
      .filter(Boolean));
    const currentNodeSnapshot = JSON.parse(JSON.stringify(sourceNode));
    if (typeof cloneNode !== 'function' || typeof setNodes !== 'function') return;

    const cloned = cloneNode(currentNodeSnapshot, existingNodeIds);
    commitClonedNodes([cloned], sourceNodes);
  }, [
    selectedNodeIds,
    nodes,
    edges,
    cloneNode,
    dispatch,
    setNodes,
    currentNode,
    reactFlow,
    commitClonedNodes,
  ]);

  const rotateSingleNode = useCallback(() => {
    if (!currentNode || typeof setNodes !== 'function') return;
    const rotable = !!currentNode?.data?.isRotable;
    if (!rotable) return;
    const currentAngle =
      typeof currentNode?.data?.angle === 'number' ? currentNode.data.angle : VERTICAL_ORIENTATION;
    const nextAngle =
      currentAngle === VERTICAL_ORIENTATION ? HORIZONTAL_ORIENTATION : VERTICAL_ORIENTATION;

    setNodes((nds) =>
      nds.map((n) =>
        n.id === currentNode.id ? { ...n, data: { ...n.data, angle: nextAngle } } : n
      )
    );

    setTimeout(() => {
      try {
        reactFlow?.updateNodeInternals?.(currentNode.id);
      } catch {}
      try {
        reactFlow?.setEdges?.((eds) =>
          Array.isArray(eds) ? eds.map((edge) => ({ ...edge })) : eds
        );
      } catch {}
      try {
        if (reactFlow && reactFlow.setNodes) {
          reactFlow.setNodes((nds) =>
            Array.isArray(nds)
              ? nds.map((n) =>
                  n.id === currentNode.id ? { ...n, selected: false } : n
                )
              : nds
          );
          setTimeout(() => {
            try {
              reactFlow.setNodes((nds) =>
                Array.isArray(nds)
                  ? nds.map((n) =>
                      n.id === currentNode.id ? { ...n, selected: true } : n
                    )
                  : nds
              );
            } catch {}
          }, 30);
        }
        dispatch(setSelectedNodeIds([currentNode.id]));
      } catch {}
    }, 100);
  }, [currentNode, dispatch, reactFlow, setNodes]);

  const rotateSelectedNodes = useCallback(() => {
    if (!Array.isArray(selectedNodeIds) || selectedNodeIds.length === 0) return;
    if (typeof setNodes !== 'function') return;

    setNodes((nds) =>
      nds.map((n) => {
        if (!selectedNodeIds.includes(n.id)) return n;
        const rotable = !!n?.data?.isRotable;
        if (!rotable) return n;
        const currentAngle =
          typeof n?.data?.angle === 'number' ? n.data.angle : VERTICAL_ORIENTATION;
        const nextAngle =
          currentAngle === VERTICAL_ORIENTATION ? HORIZONTAL_ORIENTATION : VERTICAL_ORIENTATION;
        return { ...n, data: { ...n.data, angle: nextAngle } };
      })
    );

    setTimeout(() => {
      try {
        selectedNodeIds.forEach((id) => {
          try {
            reactFlow?.updateNodeInternals?.(id);
          } catch {}
        });
      } catch {}

      try {
        reactFlow?.setEdges?.((eds) =>
          Array.isArray(eds) ? eds.map((edge) => ({ ...edge })) : eds
        );
      } catch {}

      try {
        if (reactFlow && reactFlow.setNodes) {
          reactFlow.setNodes((nds) =>
            Array.isArray(nds)
              ? nds.map((n) =>
                  selectedNodeIds.includes(n.id) ? { ...n, selected: false } : n
                )
              : nds
          );
          setTimeout(() => {
            try {
              reactFlow.setNodes((nds) =>
                Array.isArray(nds)
                  ? nds.map((n) =>
                      selectedNodeIds.includes(n.id) ? { ...n, selected: true } : n
                    )
                  : nds
              );
            } catch {}
          }, 30);
        }
        dispatch(setSelectedNodeIds(selectedNodeIds.slice()));
      } catch {}
    }, 100);
  }, [dispatch, reactFlow, selectedNodeIds, setNodes]);

  return {
    cloneSelection,
    rotateSingleNode,
    rotateSelectedNodes,
    currentNode,
    selectedNodeIds,
  };
};
